import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { AlertCircle, Bug, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import useDebugSettings, { DEBUG_DEFAULTS } from "../../hooks/useDebugSettings";
import GenerationDiagnosticsCard from "./GenerationDiagnosticsCard";
import BackgroundLogCard from "./BackgroundLogCard";

const STORAGE_KEY = "debugSettings";

const DebugSettings = () => {
  const { isAuthenticated } = useAuth();
  const syncToApi = useCallback(
    (apiFields) => {
      if (!isAuthenticated) return;
      api.updateSettings(apiFields).catch(console.error);
    },
    [isAuthenticated],
  );
  const [settings, updateSettings] = useDebugSettings({ onSync: syncToApi });
  const [storageInfo, setStorageInfo] = useState({});

  useEffect(() => {
    updateStorageInfo();
  }, [settings]);

  const updateStorageInfo = () => {
    const keys = Object.keys(localStorage);
    const items = {};
    let totalBytes = 0;
    keys.forEach((key) => {
      const val = localStorage.getItem(key);
      const bytes = new Blob([val]).size;
      totalBytes += bytes;
      items[key] = {
        size: bytes > 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`,
        preview: val.substring(0, 80) + (val.length > 80 ? "..." : ""),
      };
    });
    setStorageInfo({ items, totalBytes, count: keys.length });
  };

  const handleToggle = (checked) => {
    updateSettings({ enabled: checked });
    if (checked) {
      console.log(
        "[WorkoutAI:DEBUG] Debug logging ENABLED. All API requests/responses will be logged to console.",
      );
    } else {
      console.log("[WorkoutAI:DEBUG] Debug logging DISABLED.");
    }
  };

  const clearStorage = (key) => {
    if (key === STORAGE_KEY) {
      updateSettings(DEBUG_DEFAULTS);
      updateStorageInfo();
      return;
    }

    if (key) {
      localStorage.removeItem(key);
    }
    updateStorageInfo();
  };

  return (
    <div className="space-y-4">
      <BackgroundLogCard />
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5" />
            Debug settings
          </CardTitle>
          <CardDescription>
            Developer tools for troubleshooting AI provider issues
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Debug Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Verbose API logging</Label>
              <p className="text-sm text-muted-foreground">
                Log full request/response details to browser console
              </p>
            </div>
            <Switch
              checked={settings.enabled || false}
              onCheckedChange={handleToggle}
            />
          </div>

          {settings.enabled && (
            <div className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3 text-sm">
              <div className="flex items-center gap-2 text-yellow-600">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Debug mode active</span>
              </div>
              <p className="mt-1 text-muted-foreground">
                Open browser DevTools (F12) → Console tab to see detailed logs.
                Logs include: request URLs, headers, body, response status,
                response body, timing, and JSON extraction steps.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Which pipeline repairs and checks still fire */}
      <GenerationDiagnosticsCard />

      {/* Local Storage Inspector */}
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Local storage</CardTitle>
          <CardDescription>
            {storageInfo.count} keys,{" "}
            {storageInfo.totalBytes
              ? `${(storageInfo.totalBytes / 1024).toFixed(1)} KB total`
              : "..."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {storageInfo.items &&
              Object.entries(storageInfo.items).map(([key, info]) => (
                <div
                  key={key}
                  className="flex items-start gap-2 text-xs border-b border-border/50 pb-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{key}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {info.size}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground truncate mt-0.5 font-mono">
                      {info.preview}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-red-500"
                    onClick={() => clearStorage(key)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugSettings;
